(() => {
  if (window.wgPreorderGuardLoaded) return;
  window.wgPreorderGuardLoaded = true;

  const approved = new WeakSet();
  const approvedChanges = new WeakSet();
  const pendingChanges = new WeakSet();
  const pending = new WeakSet();
  let cartChecksPending = 0;

  const showError = (marker, message) => {
    const output = marker.querySelector('[data-wg-preorder-error]');
    output.textContent = message;
    output.hidden = false;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;

    if (
      cartChecksPending > 0 &&
      !form.querySelector('[data-wg-preorder-state]')
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const marker = form.querySelector('[data-wg-preorder-state]');
    if (!marker) return;

    if (approved.has(form)) {
      approved.delete(form);
      return;
    }

    const data = new FormData(form);
    const variantId = String(data.get('id') || '');
    const state = marker.dataset.wgPreorderState;

    if (
      variantId !== marker.dataset.wgVariantId ||
      !['in_stock', 'preorder'].includes(state)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showError(marker, marker.dataset.wgStateError);
      return;
    }

    const quantity = Number(data.get('quantity') || 1);

    if (!Number.isInteger(quantity) || quantity < 1) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showError(marker, marker.dataset.wgStockError);
      return;
    }

    if (
      marker.dataset.wgTracked !== 'true' ||
      marker.dataset.wgShipping !== 'true' ||
      marker.dataset.wgContinue !== 'true'
    ) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (pending.has(form)) return;
    pending.add(form);

    try {
      const productUrl = new URL(
        marker.dataset.wgProductUrl,
        window.location.origin
      );

      productUrl.searchParams.set('variant', variantId);
      productUrl.searchParams.set(
        'wg_preorder_check',
        String(Date.now())
      );

      const productResponse = await fetch(productUrl, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!productResponse.ok) {
        throw new Error('Product check failed');
      }

      const productHtml = new DOMParser().parseFromString(
        await productResponse.text(),
        'text/html'
      );

      const latest = productHtml.querySelector(
        `[data-wg-preorder-state][data-wg-variant-id="${variantId}"]`
      );

      if (!latest || latest.dataset.wgPreorderState !== state) {
        showError(marker, marker.dataset.wgStateError);
        return;
      }

      const initialDate = marker.querySelector(
        '[name="properties[_preorder_dispatch_date]"]'
      )?.value;

      const latestDate = latest.querySelector(
        '[name="properties[_preorder_dispatch_date]"]'
      )?.value;

      if (initialDate !== latestDate) {
        showError(marker, marker.dataset.wgStateError);
        return;
      }

      const response = await fetch(
        `${window.Shopify.routes.root}cart.js`,
        {
          credentials: 'same-origin',
          cache: 'no-store'
        }
      );

      if (!response.ok) throw new Error('Cart check failed');

      const cart = await response.json();

      const basketQuantity = cart.items
        .filter((item) =>
          String(item.variant_id) === variantId &&
          item.properties?.['Order type'] !== 'Preorder'
        )
        .reduce((sum, item) => sum + item.quantity, 0);

      const currentMarker = form.querySelector(
        '[data-wg-preorder-state]'
      );

      if (
        currentMarker !== marker ||
        String(new FormData(form).get('id')) !== variantId
      ) return;

      if (
        state === 'in_stock' &&
        quantity + basketQuantity > Number(latest.dataset.wgStock)
      ) {
        showError(marker, marker.dataset.wgStockError);
        return;
      }

      approved.add(form);

      if (
        event.submitter &&
        event.submitter.form === form &&
        !event.submitter.disabled
      ) {
        form.requestSubmit(event.submitter);
      } else {
        form.requestSubmit();
      }
    } catch {
      showError(marker, marker.dataset.wgStateError);
    } finally {
      pending.delete(form);
    }
  }, true);

  document.addEventListener('change', async (event) => {
    const input = event.target;

    if (
      !(input instanceof HTMLInputElement) ||
      !input.hasAttribute('data-line-key')
    ) return;

    if (approvedChanges.has(input)) {
      approvedChanges.delete(input);
      return;
    }

    const row =
      input.closest('line-item') ||
      input.closest('tr')?.querySelector('line-item');

    if (!row || row.dataset.wgCartContinue !== 'true') return;

    const quantity = Number(input.value);
    const original = Number(row.dataset.wgCartQuantity);

    if (quantity <= original && quantity >= 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (pendingChanges.has(input)) return;
    pendingChanges.add(input);
    cartChecksPending++;

    const reject = (message) => {
      input.value = String(original);

      let error = row.querySelector(
        '[data-wg-cart-error-message]'
      );

      if (!error) {
        error = document.createElement('p');
        error.dataset.wgCartErrorMessage = 'true';
        error.className = 'text-error text-sm';
        error.setAttribute('role', 'alert');
        (row.querySelector('.line-item-info') || row).append(error);
      }

      error.textContent = message;
    };

    try {
      if (!Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Invalid quantity');
      }

      const url = new URL(
        row.dataset.wgCartUrl,
        window.location.origin
      );

      url.searchParams.set('variant', row.dataset.wgCartVariant);
      url.searchParams.set('wg_preorder_check', String(Date.now()));

      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Product check failed');

      const html = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

      const latest = html.querySelector(
        `[data-wg-preorder-state][data-wg-variant-id="${row.dataset.wgCartVariant}"]`
      );

      const expectedState =
        row.dataset.wgCartPreorder === 'true'
          ? 'preorder'
          : 'in_stock';

      if (
        !latest ||
        latest.dataset.wgPreorderState !== expectedState
      ) {
        reject(row.dataset.wgCartError);
        return;
      }

      if (
        expectedState === 'preorder' &&
        latest.querySelector(
          '[name="properties[_preorder_dispatch_date]"]'
        )?.value !== row.dataset.wgCartDate
      ) {
        reject(row.dataset.wgCartError);
        return;
      }

      const cartResponse = await fetch(
        `${window.Shopify.routes.root}cart.js`,
        {
          cache: 'no-store',
          credentials: 'same-origin'
        }
      );

      if (!cartResponse.ok) throw new Error('Cart check failed');

      const cart = await cartResponse.json();

      const others = cart.items
        .filter((item) =>
          String(item.variant_id) === row.dataset.wgCartVariant &&
          item.key !== row.dataset.wgCartKey &&
          item.properties?.['Order type'] !== 'Preorder'
        )
        .reduce((sum, item) => sum + item.quantity, 0);

      if (
        expectedState === 'in_stock' &&
        quantity + others > Number(latest.dataset.wgStock)
      ) {
        reject(row.dataset.wgCartStockError);
        return;
      }

      if (!input.isConnected) return;

      input.value = String(quantity);
      approvedChanges.add(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } catch {
      reject(row.dataset.wgCartError);
    } finally {
      pendingChanges.delete(input);
      cartChecksPending--;
    }
  }, true);
})();